import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PrimaryButton, SecondaryButton } from '../../components/buttons/buttons';

const NotFoundPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: isDark
          ? 'linear-gradient(135deg, #1a1a1a 0%, #252525 100%)'
          : 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
        padding: { xs: 2, sm: 3 },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative dots */}
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          top: 0,
          left: 0,
          pointerEvents: 'none',
          zIndex: 0,
        }}
      >
        {[...Array(6)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              opacity: 0.1,
              animation: 'dotFloat 6s ease-in-out infinite',
              animationDelay: `${i}s`,
              ...(i === 0 && { top: '10%', left: '10%' }),
              ...(i === 1 && { top: '20%', right: '15%' }),
              ...(i === 2 && { bottom: '30%', left: '20%' }),
              ...(i === 3 && { bottom: '15%', right: '10%' }),
              ...(i === 4 && { top: '50%', left: '5%' }),
              ...(i === 5 && { top: '60%', right: '5%' }),
              '@keyframes dotFloat': {
                '0%, 100%': {
                  transform: 'translateY(0) scale(1)',
                  opacity: 0.1,
                },
                '50%': {
                  transform: 'translateY(-20px) scale(1.2)',
                  opacity: 0.3,
                },
              },
            }}
          />
        ))}
      </Box>

      <Box
        sx={{
          maxWidth: 800,
          width: '100%',
          textAlign: 'center',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Error Code */}
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: '5rem', sm: '8rem', md: '12rem' },
            fontWeight: 800,
            lineHeight: 1,
            background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            mb: 1,
            letterSpacing: '-0.05em',
          }}
        >
                    404
        </Typography>

        {/* Title */}
        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
            fontWeight: 700,
            color: 'text.primary',
            mb: 1,
            letterSpacing: '-0.02em',
          }}
        >
          {t('Looks like this route took a wrong turn!')}
        </Typography>

        {/* Description */}
        <Typography
          variant="body1"
          sx={{
            fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
            color: 'text.secondary',
            mb: 4,
            lineHeight: 1.6,
            maxWidth: 600,
            mx: 'auto',
          }}
        >
          {t('The page you\'re looking for seems to have taken a detour. Don\'t worry, our fleet is always on the move, and we\'ll help you get back on track.')}
        </Typography>

        {/* 404 Illustration */}
        <Box
          sx={{
            width: '100%',
            maxWidth: { xs: 300, sm: 400, md: 500 },
            mx: 'auto',
            mb: 4,
            opacity: 0.9,
            animation: 'float 3s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': {
                transform: 'translateY(0px)',
              },
              '50%': {
                transform: 'translateY(-20px)',
              },
            },
          }}
        >
          <img
            src="/404 error with people holding the numbers-amico.svg"
            alt="404 Error"
            style={{
              width: '100%',
              height: 'auto',
              filter: isDark ? 'brightness(0.9) contrast(1.1)' : 'none',
            }}
          />
        </Box>

        {/* Buttons */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'center',
            flexWrap: 'wrap',
            mt: 2,
          }}
        >
          <PrimaryButton onClick={() => navigate('/')}>
            {t('Go to Homepage')}
          </PrimaryButton>
          <SecondaryButton onClick={() => navigate(-1)}>
            {t('Go Back')}
          </SecondaryButton>
        </Box>
      </Box>
    </Box>
  );
};

export default NotFoundPage;
